import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { supabase } from "src/lib/supabase";

// ---------- MOCK DATA ----------
const mockTransactions = [
  { id: 1, service: "Deep House Cleaning", client: "Mary W.", date: "Mar 24, 2026", amount: "KSh 2,400", status: "Completed" },
  { id: 2, service: "Laundry & Ironing", client: "James K.", date: "Mar 23, 2026", amount: "KSh 800", status: "Completed" },
  { id: 3, service: "Plumbing Fix", client: "Aisha M.", date: "Mar 22, 2026", amount: "KSh 1,500", status: "Pending" },
  { id: 4, service: "Electrical Repair", client: "Tom N.", date: "Mar 21, 2026", amount: "KSh 2,100", status: "Completed" },
  { id: 5, service: "Painting (2 rooms)", client: "Grace O.", date: "Mar 19, 2026", amount: "KSh 4,800", status: "Paid Out" },
  { id: 6, service: "Garden Maintenance", client: "Peter L.", date: "Mar 17, 2026", amount: "KSh 1,200", status: "Pending" },
];

const mockCategories = [
  { name: "House Cleaning",    icon: "🏠", amount: 12800, pct: 45, color: "#166534" },
  { name: "Plumbing & Repairs",icon: "🔧", amount: 7900,  pct: 28, color: "#2E7D32" },
  { name: "Electrical",        icon: "⚡", amount: 5100,  pct: 18, color: "#4CAF50" },
  { name: "Gardening",         icon: "🌿", amount: 2600,  pct: 9,  color: "#81C784" },
];

const mockInsight = {
  text: "You earned 14% more this month, driven by a +45% increase in House Cleaning. Your total is 80% of your highest month ever.",
  action: "Promote House Cleaning on weekends to reach your record.",
};

const navItems = [
  { icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6", label: "Home", route: "/" },
  { icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", label: "Bookings", route: "/bookings" },
  { icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z", label: "Earnings", route: "/earnings", active: true },
  { icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z", label: "Messages", route: "/messages" },
  { icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z", label: "Settings", route: "/settings" },
];

// ---------- HELPERS ----------
const formatCurrency = (amount) => {
  if (amount === 0) return "KSh 0";
  if (amount >= 1_000_000) return `KSh ${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `KSh ${(amount / 1_000).toFixed(0)}K`;
  return `KSh ${amount.toLocaleString()}`;
};

const getDaysUntil = (targetDate) => {
  const diffTime = new Date(targetDate) - new Date();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ---------- SKELETON ----------
const SkeletonLine = ({ width = "100%", height = "1em", margin = "0" }) => (
  <div style={{ width, height, background: "#E5E7EB", borderRadius: 4, margin, animation: "shimmer 1.5s infinite" }} />
);
const SkeletonCard = ({ children }) => (
  <div style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.04)", borderRadius: 16, padding: 24 }}>{children}</div>
);

// ---------- TX ICON ----------
const txIcon = (service) => {
  const s = service.toLowerCase();
  if (s.includes("clean") || s.includes("laundry") || s.includes("ironing"))
    return <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />;
  if (s.includes("plumb") || s.includes("repair"))
    return <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />;
  if (s.includes("electric"))
    return <><polyline points="13 2 13 9 20 9" /><path d="M11 22l7-11h-7l1-9-7 11h7z" /></>;
  if (s.includes("paint"))
    return <path d="M2 13.5V19a2 2 0 002 2h16a2 2 0 002-2v-5.5M2 13.5L12 2l10 11.5M12 2v17" />;
  if (s.includes("garden"))
    return <path d="M12 2a10 10 0 0110 10c0 5.52-4.48 10-10 10S2 17.52 2 12c0-2.76 1.12-5.26 2.93-7.07M12 12V2M12 12c0-3.31 2.69-6 6-6" />;
  return <rect x="3" y="3" width="18" height="18" rx="2" />;
};

// ---------- STATUS BADGE ----------
const statusBadge = (status) => {
  if (status === "Completed") return { bg: "#E8F5E9", text: "#2E7D32" };
  if (status === "Paid Out")  return { bg: "#E8F5E9", text: "#166534" };
  if (status === "Pending")   return { bg: "#FFF3E0", text: "#ED6C02" };
  return { bg: "#F3F4F6", text: "#6B7280" };
};

// ---------- SIDEBAR CARD ----------
const SidebarCard = ({ iconPath, iconBg, iconColor, label, value, subValue }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#FFFFFF",
        border: `1px solid ${hovered ? "#D1D5DB" : "#E5E7EB"}`,
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "border-color 0.15s",
      }}
    >
      {/* LEFT: icon + label */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: iconBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
            stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            {iconPath}
          </svg>
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: "#6B7280" }}>{label}</span>
      </div>

      {/* RIGHT: value */}
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
          {value}
        </div>
        {subValue && (
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{subValue}</div>
        )}
      </div>
    </div>
  );
};

// ---------- MAIN ----------
export default function JiraniEarningsDashboard() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredNav, setHoveredNav] = useState(null);
  const [cardHovered, setCardHovered] = useState(false);
  const [hoveredCategoryIndex, setHoveredCategoryIndex] = useState(null);
  const { user, isLoggedIn, isPro } = useAuth();

  const [withdrawing, setWithdrawing] = useState(false);

  const fetchData = async () => {
    setStatus("loading");
    try {
      if (!user?.id) throw new Error("No logged-in pro");

      const { data: jobs, error } = await supabase
        .from("job_requests")
        .select(`
          id, description, estimated_price, platform_fee, payment_status,
          status, created_at, customer_id, wallet_credited,
          customer:customer_id ( full_name )
        `)
        .eq("pro_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const { data: wallet } = await supabase
        .from("pro_wallets")
        .select("balance")
        .eq("pro_id", user.id)
        .maybeSingle();

      const { data: proProfile } = await supabase
        .from("pro_profiles")
        .select("avg_rating, payout_mpesa_number")
        .eq("id", user.id)
        .maybeSingle();

      const now = new Date();
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Net = what actually reaches the pro's wallet: only rows the
      // trigger has genuinely credited count as real earnings.
      const net = j => (j.wallet_credited ? (j.estimated_price || 0) - (j.platform_fee || 0) : 0);
      const credited = (jobs || []).filter(j => j.wallet_credited);

      const thisMonth = credited.filter(j => new Date(j.created_at) >= startOfThisMonth);
      const lastMonth = credited.filter(j => {
        const d = new Date(j.created_at);
        return d >= startOfLastMonth && d < startOfThisMonth;
      });
      const thisWeek = credited.filter(j => new Date(j.created_at) >= sevenDaysAgo);

      const totalThisMonth = thisMonth.reduce((sum, j) => sum + net(j), 0);
      const totalLastMonth = lastMonth.reduce((sum, j) => sum + net(j), 0);
      const changePercent = totalLastMonth > 0
        ? Math.round(((totalThisMonth - totalLastMonth) / totalLastMonth) * 100)
        : (totalThisMonth > 0 ? 100 : 0);

      const pendingJobs = (jobs || []).filter(j => !j.wallet_credited && j.payment_status === "paid" && j.status !== "cancelled");
      const pendingTotal = pendingJobs.reduce((sum, j) => sum + ((j.estimated_price || 0) - (j.platform_fee || 0)), 0);

            const priorCustomerIds = new Set(
        (jobs || []).filter(j => new Date(j.created_at) < startOfThisMonth).map(j => j.customer_id)
      );
      const newClientIds = [...new Set(thisMonth.map(j => j.customer_id))].filter(id => !priorCustomerIds.has(id));

      // Same "new client" logic, shifted back one month, so we have a real
      // number to compare this month's new-client count against.
      const priorToLastMonthCustomerIds = new Set(
        (jobs || []).filter(j => new Date(j.created_at) < startOfLastMonth).map(j => j.customer_id)
      );
      const newClientIdsLastMonth = [...new Set(lastMonth.map(j => j.customer_id))].filter(id => !priorToLastMonthCustomerIds.has(id));
      const newClientsChange = newClientIds.length - newClientIdsLastMonth.length;
      const categoryTotals = {};
      thisMonth.forEach(j => {
        const key = j.description || "Other";
        categoryTotals[key] = (categoryTotals[key] || 0) + net(j);
      });
      const categoryColors = ["#166534", "#2E7D32", "#4CAF50", "#81C784", "#A5D6A7"];
      const categoriesReal = Object.entries(categoryTotals)
        .sort((a, b) => b[1] - a[1])
        .map(([name, amount], i) => ({
          name, amount,
          pct: totalThisMonth > 0 ? Math.round((amount / totalThisMonth) * 100) : 0,
          color: categoryColors[i % categoryColors.length],
        }));

            const insightReal = categoriesReal.length
        ? {
            text: `You earned ${Math.abs(changePercent)}% ${changePercent >= 0 ? "more" : "less"} this month, driven by ${categoriesReal[0].name}.`,
            action: `Keep taking ${categoriesReal[0].name} jobs — it's your top earner this month.`,
          }
        : {
            text: "Complete a few jobs this month to start seeing personalized insights here.",
            action: "Accept your first job this month to get going.",
          };

      setData({
        earnings: {
          total: totalThisMonth,
          changePercent,
          jobsDone: thisMonth.length,
          avgPerJob: thisMonth.length ? Math.round(totalThisMonth / thisMonth.length) : 0,
          pending: pendingTotal,
          rating: proProfile?.avg_rating || 0,
        },
        weekly: { amount: thisWeek.reduce((sum, j) => sum + net(j), 0) },
        walletBalance: wallet?.balance || 0,
        payoutPhone: proProfile?.payout_mpesa_number || "",
        newClients: { count: newClientIds.length, change: newClientsChange },
        insight: insightReal,
        categories: categoriesReal,
        transactions: (jobs || []).slice(0, 10).map(j => ({
          id: j.id,
          service: j.description || "Booking",
          client: j.customer?.full_name || "Client",
          date: new Date(j.created_at).toLocaleDateString("en-KE", { month: "short", day: "numeric", year: "numeric" }),
          amount: `KSh ${net(j).toLocaleString()}`,
          status: j.wallet_credited ? "Paid Out" : (j.payment_status === "paid" ? "Completed" : "Pending"),
        })),
      });
      setStatus("success");
    } catch (err) {
      console.error("Failed to load earnings:", err);
      setStatus("error");
    }
  };

  const handleWithdrawReal = async () => {
    if (!data?.walletBalance || withdrawing) return;
    let phone = data.payoutPhone;
    if (!phone) {
      phone = window.prompt("Enter your M-Pesa number to receive this withdrawal:");
      if (!phone) return;
    }
    setWithdrawing(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("mpesa-payout", { body: { phone } });
      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      alert("Withdrawal initiated — you'll receive an M-Pesa payment shortly.");
      fetchData();
    } catch (err) {
      alert(`Withdrawal failed: ${err.message}`);
    } finally {
      setWithdrawing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleNavClick = (route) => {
    navigate(route);
  };

  const handleViewHistory = () => {
    navigate("/earnings/history");
  };

  const handleViewAllTransactions = () => {
    navigate("/earnings/transactions");
  };

  const handleWithdraw = () => {
    navigate("/earnings/withdraw");
  };

  const handleSchedule = () => {
    navigate("/earnings/schedule");
  };

  const handleNotificationClick = () => {
    navigate("/notifications");
  };

  const handleSearchClick = () => {
    navigate("/search");
  };

  const handleProfileClick = () => {
    navigate("/settings");
  };

  const sidebarWidth = isMobile ? 60 : 80;
  const mainMarginLeft = sidebarWidth;
  const mainPadding = isMobile ? "16px" : (isTablet ? "20px 24px" : "24px 32px");
  const heroGridColumns = isMobile ? "1fr" : "2fr 1fr";
  const statsGridColumns = isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)";
  const categoryGridColumns = isMobile ? "1fr" : "repeat(2, 1fr)";
  const earningsCardTitleFontSize = isMobile ? 28 : 36;
  const payoutAmountFontSize = isMobile ? 24 : 32;

  // ── Loading ──────────────────────────────────────────────
  if (status === "loading") {
    return (
      <div style={{ display: "flex", background: "#F8FAF8", minHeight: "100vh", fontFamily: "'Inter Tight', sans-serif" }}>
        <style>{`@keyframes shimmer{0%{opacity:.4}50%{opacity:.7}100%{opacity:.4}}`}</style>
        <aside style={{ width: sidebarWidth, background: "#FFF", borderRight: "1px solid #eee" }} />
        <main style={{ flex: 1, padding: mainPadding, marginLeft: mainMarginLeft }}>
          <div style={{ marginBottom: 24 }}>
            <SkeletonLine width="200px" height="24px" />
            <SkeletonLine width="150px" height="16px" margin="8px 0 0" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: heroGridColumns, gap: 24, marginBottom: 24 }}>
            <SkeletonCard>
              <SkeletonLine width="120px" height="14px" margin="0 0 12px" />
              <SkeletonLine width="200px" height="40px" margin="0 0 8px" />
              <SkeletonLine width="160px" height="13px" margin="0 0 12px" />
              <div style={{ background: "#F9FAFB", borderRadius: 12, padding: "12px 16px", display: "grid", gridTemplateColumns: statsGridColumns, gap: 8 }}>
                {[...Array(4)].map((_, i) => (
                  <div key={i}><SkeletonLine width="60px" height="11px" margin="0 0 4px" /><SkeletonLine width="70px" height="16px" /></div>
                ))}
              </div>
            </SkeletonCard>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[...Array(3)].map((_, i) => <SkeletonCard key={i}><SkeletonLine height="36px" /></SkeletonCard>)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div style={{ display: "flex", background: "#F8FAF8", minHeight: "100vh", fontFamily: "'Inter Tight', sans-serif", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", background: "#FFF", borderRadius: 16, padding: 32, maxWidth: 400 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginTop: 16, color: "#111827" }}>Something went wrong</h3>
          <p style={{ fontSize: 14, color: "#6B7280", marginTop: 8 }}>We couldn't load your data. Please try again.</p>
          <button onClick={fetchData} style={{ marginTop: 24, background: "#166534", color: "#FFF", border: "none", borderRadius: 12, padding: "8px 24px", cursor: "pointer", fontFamily: "'Inter Tight', sans-serif", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────
  const { earnings, weekly, walletBalance, newClients, categories, transactions, insight } = data;

  const formattedTotal   = formatCurrency(earnings.total);
  const formattedWeekly  = formatCurrency(weekly.amount);
  const formattedPending = formatCurrency(earnings.pending);
  const formattedAvg     = formatCurrency(earnings.avgPerJob);
  const withdrawDisabled = !walletBalance || walletBalance <= 0 || withdrawing;
  const clientGrowthText = newClients.change > 0
    ? `↑ ${newClients.change} more than last month`
    : newClients.change < 0
      ? `↓ ${Math.abs(newClients.change)} less than last month`
      : "Same as last month";
  const topCategory = categories.reduce((max, c) => c.pct > max.pct ? c : max, categories[0]);

  return (
    <div style={{ fontFamily: "'Inter Tight', 'Inter', system-ui, sans-serif", background: "#F8FAF8", minHeight: "100vh", display: "flex" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Playfair+Display:wght@700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 999px; }
        .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      `}</style>

      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <aside style={{
        width: sidebarWidth, minWidth: sidebarWidth, background: "#FFFFFF",
        borderRight: "1px solid rgba(0,0,0,0.04)",
        display: "flex", flexDirection: "column", alignItems: "center",
        paddingTop: 24, position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
      }}>
        <div style={{
          fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: isMobile ? 9 : 11,
          color: "#166534", textAlign: "center", lineHeight: 1.2,
          letterSpacing: "-0.2px", padding: "0 6px", userSelect: "none",
        }}>
          JiraniApp.
        </div>

        <nav style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? 16 : 20, marginTop: 32, flex: 1 }}>
          {navItems.map((item, i) => (
            <button key={i}
              onClick={() => handleNavClick(item.route)}
              onMouseEnter={() => setHoveredNav(i)}
              onMouseLeave={() => setHoveredNav(null)}
              title={item.label}
              style={{
                width: isMobile ? 36 : 40, height: isMobile ? 36 : 40,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: 12,
                background: item.active ? "#EEF4EF" : hoveredNav === i ? "#F3F4F6" : "transparent",
                border: "none", cursor: "pointer", transition: "background 0.15s",
                minHeight: isMobile ? tokens.spacing[44] : "auto",
              }}
            >
              <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="none"
                stroke={item.active ? "#166534" : "#9CA3AF"}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {item.icon.split("M").filter(Boolean).map((d, j) => <path key={j} d={"M" + d} />)}
              </svg>
            </button>
          ))}
        </nav>

        <div style={{ marginBottom: 24 }}>
          <button
            onClick={handleProfileClick}
            style={{
              width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: "50%", background: "#EEF4EF",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 600, fontSize: isMobile ? 12 : 14, color: "#166534", cursor: "pointer", border: "none",
              minHeight: isMobile ? tokens.spacing[44] : "auto",
            }}
          >{user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "AM"}</button>
        </div>
      </aside>

      {/* ── MAIN ─────────────────────────────────────────────── */}
      <main style={{ marginLeft: mainMarginLeft, flex: 1, padding: mainPadding, maxWidth: isMobile ? "100%" : "calc(1240px + 80px)" }}>

        {/* Top strip */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600, color: "#0F172A", lineHeight: 1.2 }}>Good morning, {user?.name?.split(" ")[0] || "Amina"}</div>
            {!isMobile && <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>Here's your earnings overview</div>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleNotificationClick} style={{ width: 34, height: 34, borderRadius: 10, background: "#F3F4F6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 17H20L18.6 15.6A1 1 0 0118 14.8V11a6 6 0 00-5-5.9V5a1 1 0 00-2 0v.1A6 6 0 006 11v3.8a1 1 0 01-.6.8L4 17H9" />
                <path d="M13 17v1a2 2 0 01-4 0v-1" />
              </svg>
            </button>
            <button onClick={handleSearchClick} style={{ width: 34, height: 34, borderRadius: 10, background: "#F3F4F6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── HERO GRID ── */}
        <div style={{ display: "grid", gridTemplateColumns: heroGridColumns, gap: 24, marginBottom: 24 }}>

          {/* PRIMARY EARNINGS CARD */}
          <div
            onMouseEnter={() => setCardHovered(true)}
            onMouseLeave={() => setCardHovered(false)}
            style={{
              background: "#FFFFFF",
              border: "1px solid rgba(0,0,0,0.04)",
              borderRadius: 16,
              boxShadow: cardHovered ? "0px 12px 28px rgba(0,0,0,0.08)" : "0px 6px 20px rgba(0,0,0,0.04)",
              padding: isMobile ? 16 : 24,
              transition: "box-shadow 0.15s ease",
              display: "flex", flexDirection: "column", gap: 12,
            }}
          >
            {/* Section A */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#6B7280", letterSpacing: "0.2px" }}>
                Total Earnings This Month
              </span>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: earningsCardTitleFontSize, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.03em", lineHeight: 1.1 }}>
                  {formattedTotal}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 600,
                  color: earnings.changePercent >= 0 ? "#166534" : "#DC2626",
                  background: earnings.changePercent >= 0 ? "#DCFCE7" : "#FEE2E2",
                  padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap",
                }}>
                  {earnings.changePercent >= 0 ? "+" : ""}{earnings.changePercent}% vs last month
                </span>
              </div>
              {!isMobile && (
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>
                  {earnings.changePercent >= 0
                    ? `You earned ${earnings.changePercent}% more than last month`
                    : `You earned ${Math.abs(earnings.changePercent)}% less than last month`}
                </span>
              )}
            </div>

            {/* Section B — stats grid */}
            <div style={{ background: "#F9FAFB", borderRadius: 12, padding: "12px 16px", display: "grid", gridTemplateColumns: statsGridColumns }}>
              {[
                { label: "Jobs done",   value: String(earnings.jobsDone) },
                { label: "Avg per job", value: formattedAvg },
                { label: "Pending",     value: formattedPending },
                { label: "Rating",      value: `${earnings.rating} ★` },
              ].map((stat, i) => (
                <div key={i} style={{
                  display: "flex", flexDirection: "column", gap: 3,
                  paddingLeft: i === 0 ? 0 : (isMobile ? 8 : 16),
                  paddingRight: i === 3 ? 0 : (isMobile ? 8 : 16),
                  borderRight: i < 3 ? `1px solid #E5E7EB` : "none",
                }}>
                  <span style={{ fontSize: isMobile ? 10 : 11, color: "#9CA3AF" }}>{stat.label}</span>
                  <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#1F2937", letterSpacing: "-0.01em" }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDEBAR CARDS */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* This Week */}
            <SidebarCard
              iconBg="#EFF6FF" iconColor="#2563EB"
              iconPath={<>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </>}
              label="This Week"
              value={formattedWeekly}
              subValue={`↑ ${weekly.changePercent}% vs last week`}
            />

            {/* Available Balance */}
            <SidebarCard
              iconBg="#F0FDF4" iconColor="#16A34A"
              iconPath={<>
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </>}
              label="Available Balance"
              value={formatCurrency(walletBalance)}
              subValue="Ready to withdraw"
            />

            {/* New Clients */}
            <SidebarCard
              iconBg="#FAF5FF" iconColor="#9333EA"
              iconPath={<>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </>}
              label="New Clients"
              value={newClients.count}
              subValue={clientGrowthText}
            />

          </div>
        </div>

        {/* ── AI INSIGHT ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ background: "#F9FBF9", border: "1px solid rgba(0,0,0,0.04)", borderRadius: 16, padding: isMobile ? 16 : 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>AI Insights</span>
              <span style={{ fontSize: 10, fontWeight: 500, color: "#6B7280", background: "#F3F4F6", padding: "2px 6px", borderRadius: 999 }}>Beta</span>
            </div>
            <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 500, color: "#0F172A", marginBottom: 12, lineHeight: 1.4 }}>{insight.text}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="10" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#166634" }}>Recommended Action</span>
              <span style={{ fontSize: 13, color: "#374151" }}>{insight.action}</span>
            </div>
          </div>
        </div>

        {/* ── EARNINGS BREAKDOWN ── */}
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.04)", borderRadius: 16, boxShadow: "0px 6px 20px rgba(0,0,0,0.04)", padding: isMobile ? "12px 16px" : "16px 20px", marginBottom: 24 }}>

          {/* Section A — title + pill tabs */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: "#111827" }}>Earnings Breakdown</span>
            <div style={{ display: "flex", gap: 4 }}>
              {["This Month", "Last Month", "3 Months"].map((tab) => (
                <button
                  key={tab}
                  style={{
                    height: 26, padding: "0 10px",
                    borderRadius: 999,
                    border: "none", cursor: "pointer",
                    fontSize: 11, fontWeight: 500,
                    background: tab === "This Month" ? "#111827" : "transparent",
                    color: tab === "This Month" ? "#FFFFFF" : "#9CA3AF",
                    transition: "all 0.15s",
                    fontFamily: "'Inter Tight', sans-serif",
                    minHeight: isMobile ? tokens.spacing[44] : "auto",
                  }}
                >{tab}</button>
              ))}
            </div>
          </div>

          {/* Section B — segmented bar */}
          <div style={{ display: "flex", height: 9, borderRadius: 999, overflow: "hidden", marginBottom: 12 }}>
            {categories.map((cat, idx) => (
              <div key={idx} style={{ flex: cat.pct, background: cat.color, transition: "opacity 0.15s" }} />
            ))}
          </div>

          {/* Section B — 2-column legend grid */}
          <div style={{ display: "grid", gridTemplateColumns: categoryGridColumns, gap: "6px 24px" }}>
            {categories.map((cat, idx) => (
              <div
                key={idx}
                onMouseEnter={() => setHoveredCategoryIndex(idx)}
                onMouseLeave={() => setHoveredCategoryIndex(null)}
                style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                  padding: "5px 6px", borderRadius: 8,
                  background: hoveredCategoryIndex === idx ? "#F9FAFB" : "transparent",
                  transition: "background 0.15s", cursor: "default",
                }}
              >
                {/* dot + name */}
                <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
                  <span style={{ fontSize: isMobile ? 12 : 13, color: "#374151", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {cat.name}
                  </span>
                </div>
                {/* amount */}
                <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500, color: "#1F2937", marginLeft: 8, whiteSpace: "nowrap" }}>
                  {formatCurrency(cat.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── PAYOUT MODULE ── */}
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.04)", borderRadius: 16, boxShadow: "0px 6px 20px rgba(0,0,0,0.04)", padding: isMobile ? "12px 16px" : "16px 20px", marginBottom: 24, display: "flex", flexDirection: "column", gap: 10 }}>

          {/* Top row — label + history link */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#0F172A", lineHeight: 1.3 }}>Available for Withdrawal</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>Updated in real time</div>
            </div>
            <button onClick={handleViewHistory} style={{
              fontSize: 12, fontWeight: 500, color: "#166534",
              background: "transparent", border: "none", cursor: "pointer",
              padding: "2px 6px", borderRadius: 6,
              fontFamily: "'Inter Tight', sans-serif",
              minHeight: isMobile ? tokens.spacing[44] : "auto",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "#EEF4EF"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              View history →
            </button>
          </div>

          {/* Main content row — amount LEFT, buttons RIGHT */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            {/* Left: amount */}
            <div>
              <div style={{ fontSize: payoutAmountFontSize, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.03em", lineHeight: 1.1 }}>
                {formatCurrency(walletBalance)}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>Ready to withdraw</div>
            </div>

            {/* Right: CTA buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleWithdrawReal}
                disabled={withdrawDisabled}
                style={{
                  height: 36, padding: "0 16px",
                  borderRadius: 10,
                  background: withdrawDisabled ? "#E5E7EB" : "#166534",
                  border: "none",
                  color: withdrawDisabled ? "#9CA3AF" : "#FFFFFF",
                  fontSize: 13, fontWeight: 500,
                  cursor: withdrawDisabled ? "not-allowed" : "pointer",
                  fontFamily: "'Inter Tight', sans-serif",
                  transition: "background 0.15s",
                  whiteSpace: "nowrap",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}
                onMouseEnter={!withdrawDisabled ? (e => e.currentTarget.style.background = "#14532D") : undefined}
                onMouseLeave={!withdrawDisabled ? (e => e.currentTarget.style.background = "#166534") : undefined}
              >
                {withdrawing ? "Processing..." : "Withdraw Now"}
              </button>
            </div>
          </div>

          {/* Payment info row — compact single line */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid #F3F4F6", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              {data.payoutPhone ? `M-Pesa ${data.payoutPhone}` : "No M-Pesa number on file yet"}
            </span>
          </div>

        </div>

        {/* ── TRANSACTION TABLE ── */}
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.04)", borderRadius: 16, boxShadow: "0px 6px 20px rgba(0,0,0,0.04)", overflow: "hidden" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "12px 16px 0" : "16px 20px 0", flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: "#111827" }}>Recent Transactions</span>
            <button onClick={handleViewAllTransactions} style={{ fontSize: 12, fontWeight: 500, color: "#166534", background: "transparent", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 6, fontFamily: "'Inter Tight', sans-serif", minHeight: isMobile ? tokens.spacing[44] : "auto" }}
              onMouseEnter={e => e.currentTarget.style.background = "#EEF4EF"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              View all →
            </button>
          </div>

          {/* Column headers - hide on mobile */}
          {!isMobile && (
            <div style={{
              display: "flex", alignItems: "center",
              padding: "10px 20px",
              borderBottom: "1px solid #F3F4F6",
              marginTop: 12,
            }}>
              <div style={{ flex: "0 0 32px", marginRight: 12 }} />
              <div style={{ flex: "1 1 0", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.6px" }}>Transaction</div>
              <div style={{ flex: "0 0 110px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.6px" }}>Via</div>
              <div style={{ flex: "0 0 100px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.6px", textAlign: "right" }}>Amount</div>
              <div style={{ flex: "0 0 90px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.6px", textAlign: "right" }}>Status</div>
            </div>
          )}

          {/* Rows */}
          {transactions.map((tx) => {
            const badge = statusBadge(tx.status);
            const icon = txIcon(tx.service);
            return (
              <div
                key={tx.id}
                onMouseEnter={() => setHoveredRow(tx.id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => handleNavClick(`/earnings/transaction/${tx.id}`)}
                style={{
                  display: "flex", alignItems: "center",
                  padding: isMobile ? "12px 16px" : "11px 20px",
                  borderBottom: "1px solid #F9FAFB",
                  background: hoveredRow === tx.id ? "#FAFAFA" : "#FFFFFF",
                  transition: "background 0.12s",
                  cursor: "pointer",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: isMobile ? 8 : 0,
                }}
              >
                {/* Icon */}
                <div style={{
                  flex: "0 0 32px", height: 32, borderRadius: 9,
                  background: "#F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginRight: isMobile ? 12 : 12,
                }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none"
                    stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {icon}
                  </svg>
                </div>

                {/* Service + date */}
                <div style={{ flex: isMobile ? "1 1 0" : "1 1 0", minWidth: 0 }}>
                  <div className="truncate" style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500, color: "#111827" }} title={tx.service}>{tx.service}</div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: "#9CA3AF", marginTop: 1 }}>{tx.date} · {tx.client}</div>
                </div>

                {/* Via */}
                <div style={{ flex: isMobile ? "0 0 auto" : "0 0 110px", fontSize: isMobile ? 12 : 13, color: "#6B7280", minWidth: isMobile ? 60 : "auto" }}>M-Pesa</div>

                {/* Amount */}
                <div style={{ flex: isMobile ? "0 0 auto" : "0 0 100px", fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#111827", textAlign: isMobile ? "left" : "right", minWidth: isMobile ? 80 : "auto" }}>{tx.amount}</div>

                {/* Status badge */}
                <div style={{ flex: isMobile ? "0 0 auto" : "0 0 90px", textAlign: isMobile ? "left" : "right" }}>
                  <span style={{
                    display: "inline-block",
                    padding: "3px 9px", borderRadius: 999,
                    fontSize: isMobile ? 10 : 11, fontWeight: 500,
                    background: badge.bg, color: badge.text,
                  }}>
                    {tx.status}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Footer link - mobile friendly */}
          <div style={{ padding: isMobile ? "10px 16px" : "12px 20px", textAlign: "right" }}>
            <button onClick={handleViewAllTransactions} style={{ fontSize: 12, color: "#9CA3AF", background: "transparent", border: "none", cursor: "pointer", fontFamily: "'Inter Tight', sans-serif", minHeight: isMobile ? tokens.spacing[44] : "auto" }}
              onMouseEnter={e => e.currentTarget.style.color = "#6B7280"}
              onMouseLeave={e => e.currentTarget.style.color = "#9CA3AF"}
            >
              View all transactions →
            </button>
          </div>

        </div>

      </main>
    </div>
  );
}